import React, { useCallback, useEffect, useMemo, useState } from 'react';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOffOutlinedIcon from '@mui/icons-material/FolderOffOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ViewListIcon from '@mui/icons-material/ViewList';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  DRAG_MIME,
  Folder,
  FolderNode,
  normalizeFolderTree,
} from './FoldersUtil';
import { useUserData } from '../App/Component/UserDataProvider';
import { useDocumentLibrary } from '../DocumentLibrary/Component/DocumentLibraryProvider';
import DocumentLibraryTable from '../DocumentLibrary/Component/TableComponents/DocumentLibraryTable';
import { getContractFolderId } from '../DocumentLibrary/Component/TableComponents/helper';
import ReusableConfirmationModal from '../RiverusUI/Components/ReusableConfirmationModal';
import { fetchAllContractCategory } from '../Services/Approval';
import {
  bulkDeleteFolders,
  createFolder,
  editFolder,
  fetchFoldersTree,
  moveToFolders,
  removeContractsFromFolder,
} from '../Services/ContractsFolders';
import { fetchDocLibContracts } from '../Services/DocumentLibrary';
import { fetchProductMaster, fetchProjects } from '../Services/ProjectConfig';
import { getContractTypes } from '../Services/Template';
import { fetchTerritoryMaster } from '../Services/TerritoryMaster';
import { QueryKeyGenerator } from '../Utils/QueryKeyGenerator';

const ALL_ID = 'all';
const NONE_ID = 'none';
const ALL_LABEL = 'All Contracts';
const STANDALONE_LABEL = 'Standalone Contracts';
const ACCENT = '#6D264C';
const ALREADY_IN_FOLDER = 'ALREADY_IN_FOLDER';

const initialFilter = {
  project: null,
  title: null,
  groups: null,
  contracttype: null,
  contracting_parties: null,
  status: null,
  contractcategory: null,
  productmasters: null,
  territory: null,
  source_type: null,
  reference_id: null,
};

const EMPTY_TREE: FolderNode[] = [];
const EMPTY_FLAT: Folder[] = [];

type MoveTarget = { folderId: string } | { newName: string };
type MoveVars = { ids: string[]; target: MoveTarget };
type PendingMove = MoveVars & { conflicts: string[] };
type NameDialogState =
  | { open: false }
  | { open: true; mode: 'create'; parentId: string | null }
  | { open: true; mode: 'rename'; folder: Folder };

const emptySelection = (): GridRowSelectionModel => ({
  type: 'include',
  ids: new Set(),
});

const toggleInSet = (
  setter: React.Dispatch<React.SetStateAction<Set<string>>>,
  id: string
) =>
  setter((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

const descendantIds = (id: string, folders: Folder[]): string[] =>
  folders.flatMap((f) =>
    f.parent_id === id ? [f.id, ...descendantIds(f.id, folders)] : []
  );

const readDraggedIds = (e: React.DragEvent): string[] => {
  try {
    return JSON.parse(e.dataTransfer.getData(DRAG_MIME) || '[]');
  } catch {
    return [];
  }
};

const errorText = (err: any, fallback: string) =>
  err?.response?.data?.detail ||
  err?.response?.data?.message ||
  err?.response?.data?.name?.[0] ||
  fallback;

const FolderNameDialog: React.FC<{
  state: NameDialogState;
  folders: Folder[];
  loading: boolean;
  onClose: () => void;
  onSubmit: (name: string) => void;
}> = ({ state, folders, loading, onClose, onSubmit }) => {
  const [name, setName] = useState('');
  const isRename = state.open && state.mode === 'rename';

  useEffect(() => {
    if (state.open) setName(state.mode === 'rename' ? state.folder.name : '');
  }, [state]);

  const trimmed = name.trim();
  const parentId = !state.open
    ? null
    : state.mode === 'create'
      ? state.parentId
      : state.folder.parent_id;
  const selfId = state.open && state.mode === 'rename' ? state.folder.id : null;

  const duplicate = folders.some(
    (f) =>
      f.id !== selfId &&
      (f.parent_id ?? null) === (parentId ?? null) &&
      f.name.trim().toLowerCase() === trimmed.toLowerCase()
  );
  const canSubmit = !!trimmed && !duplicate && !loading;

  return (
    <Dialog open={state.open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{isRename ? 'Rename folder' : 'New folder'}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          margin="dense"
          label="Folder name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canSubmit) onSubmit(trimmed);
          }}
          error={duplicate}
          helperText={
            duplicate ? 'A folder with this name already exists here' : ' '
          }
          inputProps={{ maxLength: 100 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={() => onSubmit(trimmed)}
        >
          {isRename ? 'Rename' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const MoveDialog: React.FC<{
  open: boolean;
  count: number;
  tree: FolderNode[];
  folders: Folder[];
  loading: boolean;
  onClose: () => void;
  countAlreadyIn: (folderId: string) => number;
  onMove: (target: MoveTarget) => void;
}> = ({
  open,
  count,
  tree,
  folders,
  loading,
  countAlreadyIn,
  onClose,
  onMove,
}) => {
  const [target, setTarget] = useState('');
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const { userIsSuperAdmin } = useUserData();

  useEffect(() => {
    if (open) {
      setTarget('');
      setCreating(false);
      setNewName('');
    }
  }, [open]);

  const flat = useMemo(() => {
    const out: { folder: Folder; depth: number }[] = [];
    const walk = (nodes: FolderNode[], depth: number) =>
      nodes.forEach((n) => {
        out.push({ folder: n, depth });
        walk(n.children, depth + 1);
      });
    walk(tree, 0);
    return out;
  }, [tree]);

  const duplicateNew =
    creating &&
    folders.some(
      (f) =>
        !f.parent_id &&
        f.name.trim().toLowerCase() === newName.trim().toLowerCase()
    );

  const alreadyInTarget = !creating && target ? countAlreadyIn(target) : 0;
  const movable = count - alreadyInTarget;
  const canMove =
    (creating ? !!newName.trim() && !duplicateNew : !!target) && movable > 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Move to folder</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {count} contract(s) selected
        </Typography>
        <List
          dense
          sx={{
            maxHeight: 300,
            overflow: 'auto',
            border: '1px solid #e4e4e4',
            borderRadius: 1,
          }}
        >
          {flat.length === 0 && (
            <ListItemText
              sx={{ px: 2 }}
              secondary="No folders yet. Create one below."
            />
          )}
          {flat.map(({ folder, depth }) => {
            const already = countAlreadyIn(folder.id);
            const full = count > 0 && already === count;
            return (
              <ListItemButton
                key={folder.id}
                disabled={full}
                selected={!creating && target === folder.id}
                onClick={() => {
                  setCreating(false);
                  setTarget(folder.id);
                }}
                sx={{ pl: 2 + depth * 3 }}
              >
                <FolderIcon fontSize="small" sx={{ mr: 1, color: ACCENT }} />
                <ListItemText
                  primary={folder.name}
                  primaryTypographyProps={{ noWrap: true, title: folder.name }}
                  secondary={
                    full
                      ? 'Already in this folder'
                      : already
                        ? `${already} already here, will be skipped`
                        : undefined
                  }
                />
              </ListItemButton>
            );
          })}
        </List>
        {alreadyInTarget > 0 && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {alreadyInTarget} of the selected contract(s) are already in “
            {folders.find((f) => f.id === target)?.name}” and will not be added
            again.
          </Alert>
        )}
        {creating ? (
          <TextField
            autoFocus
            fullWidth
            size="small"
            margin="dense"
            label="New folder name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            error={duplicateNew}
            helperText={
              duplicateNew ? 'A folder with this name already exists' : ' '
            }
          />
        ) : (
          userIsSuperAdmin && (
            <Button
              startIcon={<CreateNewFolderOutlinedIcon />}
              onClick={() => setCreating(true)}
              sx={{ mt: 1, textTransform: 'none' }}
            >
              Create new folder
            </Button>
          )
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={loading || count === 0 || !canMove}
          onClick={() =>
            onMove(
              creating ? { newName: newName.trim() } : { folderId: target }
            )
          }
        >
          Move {movable} contract(s)
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DocumentFoldersComponent: React.FC = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { userIsViewer, userIsSuperAdmin } = useUserData();
  const canManage = !userIsViewer;
  const { filters, setFilters, sorting, setSorting } = useDocumentLibrary();

  const [activeId, setActiveId] = useState(ALL_ID);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [folderSearch, setFolderSearch] = useState('');
  const [debouncedFolderSearch, setDebouncedFolderSearch] = useState('');
  const [checkedFolders, setCheckedFolders] = useState<Set<string>>(new Set());
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ el: HTMLElement; folder: Folder } | null>(
    null
  );
  // table state
  const [pageNumber, setPageNumber] = useState(0);
  const [selection, setSelection] =
    useState<GridRowSelectionModel>(emptySelection());
  const [refreshKey, setRefreshKey] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [titleSearchValue, setTitleSearchValue] = useState('');
  const [refIdSearchValue, setRefIdSearchValue] = useState('');
  const [partySearchValue, setPartySearchValue] = useState('');
  // dialogs
  const [nameDialog, setNameDialog] = useState<NameDialogState>({
    open: false,
  });
  const [deleteIds, setDeleteIds] = useState<string[] | null>(null);
  const [moveOpen, setMoveOpen] = useState(false);
  const [pendingMove, setPendingMove] = useState<PendingMove | null>(null);
  const [removeIds, setRemoveIds] = useState<string[] | null>(null);

  useEffect(() => {
    return () => {
      setFilters(initialFilter);
      setSorting([]);
    };
  }, []);

  useEffect(() => {
    const t = setTimeout(
      () => setDebouncedFolderSearch(folderSearch.trim()),
      400
    );
    return () => clearTimeout(t);
  }, [folderSearch]);

  const { data: contracts } = useQuery({
    queryKey: QueryKeyGenerator.getChoiceFetchingQuery(),
    queryFn: getContractTypes,
    select: (response: any) => response.results,
  });

  const { data: contractCategory } = useQuery({
    queryKey: ['document_contract_Category'],
    queryFn: fetchAllContractCategory,
    select: (response: any) => response.results || [],
  });

  const { data: projectData } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => await fetchProjects(),
    select: (response: any) => response?.results,
  });

  const { data: productMasterData } = useQuery({
    queryKey: ['product_master_data'],
    queryFn: fetchProductMaster,
    select: (response: any) =>
      response.results.filter((data: any) => data.name !== ''),
  });

  const { data: territoryMasterData } = useQuery({
    queryKey: ['territory_master'],
    queryFn: () => fetchTerritoryMaster({}),
    select: (response: any) => response.results,
  });

  const {
    data: folderData,
    isLoading: loadingFolders,
    isFetching: fetchingFolders,
  } = useQuery({
    queryKey: ['umbrella_folders'],
    queryFn: () => fetchFoldersTree(),
    select: normalizeFolderTree,
  });

  const { data: searchData, isFetching: searchingFolders } = useQuery({
    queryKey: ['umbrella_folders', 'search', debouncedFolderSearch],
    queryFn: () => fetchFoldersTree(debouncedFolderSearch),
    select: normalizeFolderTree,
    enabled: !!debouncedFolderSearch,
  });

  const {
    data: contractTableData,
    isLoading: isLoadingTable,
    isFetching,
    isError,
  } = useQuery({
    queryKey: ['folder_contracts', activeId, filters, pageNumber, sorting],
    queryFn: async () => {
      let filterParam = '';
      for (const key in filters) {
        const value = filters[key];
        if (value !== null && value !== undefined) {
          const text = Array.isArray(value) ? value.join(',') : String(value);
          filterParam += `&${key}=${encodeURIComponent(text)}`;
        }
      }
      let sortingParam = '?ordering=-created_on';
      if (sorting?.length > 0) {
        const s = sorting[0];
        sortingParam = `?ordering=${s.sort === 'asc' ? '' : '-'}${s.field}`;
      }
      const folderParam =
        activeId === ALL_ID
          ? ''
          : activeId === NONE_ID
            ? '&is_folder=false'
            : `&folder=${encodeURIComponent(activeId)}`;
      return fetchDocLibContracts(
        `${sortingParam}&page=${pageNumber + 1}${filterParam}${folderParam}`
      );
    },
    staleTime: 1000,
    retry: false,
    placeholderData: (prev) => prev,
  });

  const tree = folderData?.tree ?? EMPTY_TREE;
  const folders = folderData?.flat ?? EMPTY_FLAT;
  const isSearching = !!debouncedFolderSearch;
  const displayTree = isSearching ? (searchData?.tree ?? EMPTY_TREE) : tree;

  const byId = useMemo(() => new Map(folders.map((f) => [f.id, f])), [folders]);
  const activeFolder = byId.get(activeId);

  // If the open folder disappeared (deleted by someone else), fall back to All
  useEffect(() => {
    if (
      activeId !== ALL_ID &&
      activeId !== NONE_ID &&
      !loadingFolders &&
      !byId.has(activeId)
    ) {
      setActiveId(ALL_ID);
    }
  }, [activeId, byId, loadingFolders]);

  useEffect(() => {
    if (isError) {
      enqueueSnackbar(
        'Oops... something went wrong. Please reload the page to try again!',
        {
          variant: 'error',
        }
      );
    }
  }, [isError]);

  const selectedRowData = useMemo(
    () =>
      contractTableData?.results?.filter((r: any) =>
        selection?.ids?.has(r?.id)
      ),
    [contractTableData?.results, selection]
  );
  const selectedIds = useMemo(
    () => [...selection.ids].map(String),
    [selection]
  );

  // contract id -> id of the folder it is in (null = unfiled)
  const folderOfContract = useMemo(() => {
    const m = new Map<string, string | null>();
    (contractTableData?.results ?? []).forEach((r: any) =>
      m.set(String(r.id), getContractFolderId(r))
    );
    return m;
  }, [contractTableData?.results]);

  const folderIdOf = (id: string) => folderOfContract.get(id) ?? null;
  const countSelectedIn = (folderId: string) =>
    selectedIds.filter((id) => folderIdOf(id) === folderId).length;

  const handleStatusChange = useCallback((status: any) => {
    const { isProcessing, notStarted, failed } = status;
    setButtonDisabled(notStarted || isProcessing || failed);
  }, []);

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['umbrella_folders'] });
    queryClient.invalidateQueries({ queryKey: ['folder_contracts'] });
    queryClient.invalidateQueries({ queryKey: ['contract_table_data'] });
    queryClient.invalidateQueries({ queryKey: ['contract_header_count'] });
    setRefreshKey((k) => !k);
  }, [queryClient]);

  const finishContractChange = () => {
    setSelection(emptySelection());
    refresh();
  };

  const switchFolder = (id: string) => {
    setActiveId(id);
    setPageNumber(0);
    setSelection(emptySelection());
    setFilters(initialFilter);
    setTitleSearchValue('');
    setRefIdSearchValue('');
    setPartySearchValue('');
  };

  const { mutate: saveName, isPending: savingName } = useMutation({
    mutationFn: async (name: string) => {
      if (!nameDialog.open) return null;
      return nameDialog.mode === 'rename'
        ? editFolder({ id: nameDialog.folder.id, body: { name } })
        : createFolder({ name, parent: nameDialog.parentId });
    },
    onSuccess: (res: any) => {
      if (nameDialog.open && nameDialog.mode === 'create') {
        enqueueSnackbar('Folder created', { variant: 'success' });
        const { parentId } = nameDialog;
        if (parentId) setExpanded((p) => new Set(p).add(parentId));
        const newId = res?.id ?? res?.data?.id;
        if (newId != null) switchFolder(String(newId));
      } else {
        enqueueSnackbar('Folder renamed', { variant: 'success' });
      }
      setNameDialog({ open: false });
      refresh();
    },
    onError: (e) =>
      enqueueSnackbar(errorText(e, 'Could not save folder'), {
        variant: 'error',
      }),
  });

  const { mutate: removeFolders, isPending: deleting } = useMutation({
    mutationFn: (ids: string[]) => bulkDeleteFolders({ folder_ids: ids }),
    onSuccess: (_r, ids) => {
      enqueueSnackbar(`${ids.length} folder(s) deleted.`, {
        variant: 'success',
      });
      const gone = new Set(
        ids.flatMap((id) => [id, ...descendantIds(id, folders)])
      );
      if (gone.has(activeId)) switchFolder(ALL_ID);
      setCheckedFolders(new Set());
      setDeleteIds(null);
      refresh();
    },
    onError: (e) =>
      enqueueSnackbar(errorText(e, 'Failed to delete folders'), {
        variant: 'error',
      }),
  });

  const { mutateAsync: removeFromFolder, isPending: unfiling } = useMutation({
    mutationFn: (vars: { ids: string[]; silent?: boolean }) =>
      removeContractsFromFolder({ contract_ids: vars.ids }),
    onSuccess: (_r, vars) => {
      if (vars.silent) return;
      enqueueSnackbar(
        `${vars.ids.length} contract(s) removed from folder. They are now in ${STANDALONE_LABEL}.`,
        { variant: 'success' }
      );
      setRemoveIds(null);
      finishContractChange();
    },
    onError: (e, vars) => {
      if (vars.silent) return;
      setRemoveIds(null);
      enqueueSnackbar(
        errorText(e, 'Could not remove contracts from the folder'),
        { variant: 'error' }
      );
    },
  });

  const { mutate: moveContracts, isPending: moving } = useMutation({
    mutationFn: async ({ ids: selected, target }: MoveVars) => {
      let folderId: string;
      let folderName: string;
      if ('newName' in target) {
        const created: any = await createFolder({
          name: target.newName,
          parent: null,
        });
        folderId = String(created?.id ?? created?.data?.id);
        folderName = target.newName;
      } else {
        folderId = target.folderId;
        folderName = byId.get(folderId)?.name ?? 'this folder';
      }
      // a contract is never added to the same folder twice
      const ids = selected.filter((id) => folderIdOf(id) !== folderId);
      if (ids.length === 0) {
        throw new Error(`${ALREADY_IN_FOLDER}:${folderName}`);
      }
      // contracts that are in some folder now, grouped by it (for rollback)
      const bySource = new Map<string, string[]>();
      ids.forEach((id) => {
        const src = folderIdOf(id);
        if (src) bySource.set(src, [...(bySource.get(src) ?? []), id]);
      });
      const inFolder = [...bySource.values()].flat();
      // 1. take them out of their current folder
      if (inFolder.length) {
        await removeFromFolder({ ids: inFolder, silent: true });
      }
      // 2. add everything to the target; put them back if that fails
      try {
        await moveToFolders(folderId, { contract_ids: ids });
      } catch (err) {
        for (const [src, cids] of bySource) {
          await moveToFolders(src, { contract_ids: cids }).catch(() => {});
        }
        throw err;
      }
      return {
        moved: ids.length,
        skipped: selected.length - ids.length,
        folderName,
      };
    },
    onSuccess: ({ moved, skipped, folderName }) => {
      enqueueSnackbar(
        skipped
          ? `${moved} contract(s) moved to “${folderName}”. ${skipped} already in this folder, skipped.`
          : `${moved} contract(s) moved to “${folderName}”`,
        { variant: 'success' }
      );
      setPendingMove(null);
      setMoveOpen(false);
      finishContractChange();
    },
    onError: (e: any) => {
      setPendingMove(null);
      if (e?.message?.startsWith(ALREADY_IN_FOLDER)) {
        const name = e.message.split(':').slice(1).join(':');
        enqueueSnackbar(
          `Selected contract(s) are already in “${name}”. A contract cannot be added to the same folder twice.`,
          { variant: 'warning' }
        );
      } else {
        enqueueSnackbar(
          errorText(
            e,
            'Could not move contracts. You need edit access to all of them.'
          ),
          { variant: 'error' }
        );
      }
    },
  });

  const requestMove = (ids: string[], target: MoveTarget) => {
    const targetId = 'folderId' in target ? target.folderId : null;
    const conflicts = ids.filter((id) => {
      const src = folderIdOf(id);
      return !!src && src !== targetId;
    });
    if (conflicts.length) setPendingMove({ ids, target, conflicts });
    else moveContracts({ ids, target });
  };

  const requestRemove = (ids: string[]) => {
    const inFolder = ids.filter((id) => !!folderIdOf(id));
    if (inFolder.length) setRemoveIds(inFolder);
    else
      enqueueSnackbar('Selected contract(s) are not in any folder', {
        variant: 'info',
      });
  };

  const conflictSummary = useMemo(() => {
    const counts = new Map<string, number>();
    (pendingMove?.conflicts ?? []).forEach((id) => {
      const name = byId.get(folderOfContract.get(id) ?? '')?.name ?? 'Unknown';
      counts.set(name, (counts.get(name) ?? 0) + 1);
    });
    return [...counts];
  }, [pendingMove, byId, folderOfContract]);

  // If a parent and its child are both checked, only send the parent
  const topLevelChecked = useMemo(
    () =>
      [...checkedFolders].filter((id) => {
        let cur = byId.get(id)?.parent_id ?? null;
        while (cur) {
          if (checkedFolders.has(cur)) return false;
          cur = byId.get(cur)?.parent_id ?? null;
        }
        return true;
      }),
    [checkedFolders, byId]
  );

  const dropProps = (folderId: string) =>
    canManage
      ? {
          onDragOver: (e: React.DragEvent) => {
            if (e.dataTransfer.types.includes(DRAG_MIME)) {
              e.preventDefault();
              setDropTarget(folderId);
            }
          },
          onDragLeave: () =>
            setDropTarget((cur) => (cur === folderId ? null : cur)),
          onDrop: (e: React.DragEvent) => {
            e.preventDefault();
            setDropTarget(null);
            const ids = readDraggedIds(e);
            if (ids.length) requestMove(ids, { folderId });
          },
        }
      : {};

  /* ---------------- render helpers ---------------- */

  const rowSx = (id: string, selected: boolean) => ({
    cursor: 'pointer',
    borderRadius: '8px',
    minHeight: 36,
    bgcolor: selected ? '#e8d9e1' : 'transparent',
    fontWeight: selected ? 700 : 400,
    border:
      dropTarget === id ? `2px dashed ${ACCENT}` : '2px solid transparent',
    '&:hover': { bgcolor: selected ? '#e8d9e1' : '#f6eef2' },
  });

  const renderNode = (n: FolderNode, depth: number): React.ReactNode => {
    const isOpen = isSearching || expanded.has(n.id);
    const selected = activeId === n.id;

    return (
      <React.Fragment key={n.id}>
        <Stack
          direction="row"
          alignItems="center"
          onClick={() => switchFolder(n.id)}
          {...dropProps(n.id)}
          sx={{
            ...rowSx(n.id, selected),
            pl: `${4 + depth * 20}px`,
            pr: 0.5,
            '& .row-actions': { visibility: 'hidden' },
            '&:hover .row-actions': { visibility: 'visible' },
          }}
        >
          <Box sx={{ width: 28, display: 'flex' }}>
            {n.children?.length > 0 && (
              <IconButton
                size="small"
                aria-label={isOpen ? 'Collapse' : 'Expand'}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleInSet(setExpanded, n.id);
                }}
              >
                {isOpen ? (
                  <ArrowDropDownIcon fontSize="small" />
                ) : (
                  <ArrowRightIcon fontSize="small" />
                )}
              </IconButton>
            )}
          </Box>
          {canManage && userIsSuperAdmin && (
            <Checkbox
              size="small"
              checked={checkedFolders.has(n.id)}
              onClick={(e) => e.stopPropagation()}
              onChange={() => toggleInSet(setCheckedFolders, n?.id)}
              inputProps={{ 'aria-label': `Select folder ${n?.name}` }}
              sx={{ p: 0.5 }}
            />
          )}
          <FolderIcon fontSize="small" sx={{ mx: 0.75, color: ACCENT }} />
          <Tooltip title={n?.name} placement="right" enterDelay={300}>
            <Typography
              noWrap
              sx={{ flex: 1, fontSize: 15, fontWeight: 'inherit' }}
            >
              {n?.name}
            </Typography>
          </Tooltip>
          <Typography
            variant="caption"
            sx={{
              bgcolor: selected ? '#fff' : '#f3e6ec',
              borderRadius: '10px',
              px: 1,
              mr: 0.5,
            }}
          >
            {n?.contract_count}
          </Typography>
          {canManage && userIsSuperAdmin && (
            <IconButton
              className="row-actions"
              size="small"
              aria-label="Folder actions"
              onClick={(e) => {
                e.stopPropagation();
                setMenu({ el: e.currentTarget, folder: n });
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
        {isOpen && n?.children?.map((c) => renderNode(c, depth + 1))}
      </React.Fragment>
    );
  };

  const fixedRow = (id: string, label: string, icon: React.ReactNode) => (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      onClick={() => switchFolder(id)}
      sx={{ ...rowSx(id, activeId === id), px: 1.5 }}
    >
      {icon}
      <Typography sx={{ fontSize: 15, fontWeight: 'inherit' }}>
        {label}
      </Typography>
    </Stack>
  );

  const title =
    activeId === ALL_ID
      ? ALL_LABEL
      : activeId === NONE_ID
        ? STANDALONE_LABEL
        : (activeFolder?.name ?? '');

  const anySelectedInFolder = selectedIds.some((id) => !!folderIdOf(id));

  return (
    <>
      <Tooltip title="Refresh folders and contracts">
        <span>
          <IconButton
            aria-label="Refresh"
            onClick={finishContractChange}
            disabled={fetchingFolders || isFetching}
            sx={{ height: '40px', width: '40px' }}
          >
            <RefreshIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        gap="20px"
        alignItems="flex-start"
        sx={{ p: 0.5 }}
      >
        {/* LEFT: folder tree */}
        <Box
          sx={{
            width: { xs: '100%', md: 300 },
            flexShrink: 0,
            bgcolor: '#fff',
            border: '1px solid #e4e4e4',
            borderRadius: '12px',
            p: 1.5,
          }}
        >
          <Stack direction="row" spacing={1} mb={1.5}>
            <TextField
              size="small"
              placeholder="Search folders"
              value={folderSearch}
              onChange={(e) => setFolderSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1 }}
            />
            {canManage && userIsSuperAdmin && (
              <Button
                variant="outlined"
                startIcon={<CreateNewFolderOutlinedIcon />}
                onClick={() =>
                  setNameDialog({
                    open: true,
                    mode: 'create',
                    parentId: activeFolder?.id ?? null,
                  })
                }
                sx={{
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  height: '40px',
                }}
              >
                New
              </Button>
            )}
          </Stack>

          {topLevelChecked.length > 0 && userIsSuperAdmin && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                bgcolor: '#f6eef2',
                borderRadius: '8px',
                px: 1.5,
                py: 0.5,
                mb: 1,
              }}
            >
              <Typography variant="body2" fontWeight="bold">
                {checkedFolders.size} Selected
              </Typography>
              <Stack direction="row">
                <Button
                  size="small"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => setDeleteIds(topLevelChecked)}
                  sx={{ padding: '10px', margin: 0 }}
                >
                  Delete
                </Button>
                <Button
                  size="small"
                  onClick={() => setCheckedFolders(new Set())}
                  sx={{ padding: '10px', margin: 0 }}
                >
                  Clear
                </Button>
              </Stack>
            </Stack>
          )}
          <Stack spacing="2px">
            {fixedRow(
              ALL_ID,
              ALL_LABEL,
              <ViewListIcon fontSize="small" sx={{ color: ACCENT }} />
            )}
            <Box
              sx={{
                height: 'calc(100vh - 300px)',
                minHeight: 240,
                overflowY: 'auto',
                overflowX: 'hidden',
                pr: 0.5,
              }}
            >
              <Stack spacing="2px">
                {displayTree.map((n) => renderNode(n, 0))}
                {!loadingFolders &&
                  !searchingFolders &&
                  displayTree.length === 0 && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ px: 1.5, py: 1 }}
                    >
                      {isSearching
                        ? 'No folders match your search.'
                        : 'No folders yet. Use New to create one.'}
                    </Typography>
                  )}
              </Stack>
            </Box>
            <Box sx={{ borderTop: '1px solid #eee', mt: 0.5, pt: 0.5 }}>
              {fixedRow(
                NONE_ID,
                STANDALONE_LABEL,
                <FolderIcon fontSize="small" sx={{ color: '#888' }} />
              )}
            </Box>
          </Stack>
        </Box>
        {/* RIGHT: the same contracts table, scoped to the selected folder */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            bgcolor: '#fff',
            border: '1px solid #e4e4e4',
            borderRadius: '12px',
            overflow: 'hidden',
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
            p={2}
          >
            <Box>
              <Typography variant="h6" fontWeight="bold" title={title}>
                {title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {contractTableData?.count ?? 0} contracts
              </Typography>
            </Box>
            {canManage && selectedIds.length > 0 && (
              <Stack direction="row" gap={1}>
                {anySelectedInFolder && (
                  <Button
                    variant="outlined"
                    startIcon={<FolderOffOutlinedIcon />}
                    disabled={buttonDisabled}
                    onClick={() => requestRemove(selectedIds)}
                    sx={{ textTransform: 'none' }}
                  >
                    Remove from folder
                  </Button>
                )}
                <Button
                  variant="contained"
                  startIcon={<DriveFileMoveOutlinedIcon />}
                  disabled={buttonDisabled}
                  onClick={() => setMoveOpen(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Move to folder
                </Button>
              </Stack>
            )}
          </Stack>
          <Box sx={{ px: 1 }}>
            <DocumentLibraryTable
              key={activeId}
              folderView
              tableData={contractTableData}
              pageNumber={pageNumber}
              setPageNumberChange={setPageNumber}
              setSelectedRow={setSelection}
              setFilters={setFilters}
              filters={filters}
              isLoading={isLoadingTable || isFetching}
              setSorting={setSorting}
              refreshKey={refreshKey}
              selectedRowData={selectedRowData}
              onStatusChange={handleStatusChange}
              titleSearchValue={titleSearchValue}
              setTitleSearchValue={setTitleSearchValue}
              refIdSearchValue={refIdSearchValue}
              setRefIdSearchValue={setRefIdSearchValue}
              partySearchValue={partySearchValue}
              setPartySearchValue={setPartySearchValue}
              contracts={contracts}
              contractCategory={contractCategory}
              projectData={projectData}
              productMasterData={productMasterData}
              territoryMasterData={territoryMasterData}
            />
          </Box>
          {canManage && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', px: 2, py: 1 }}
            >
              Tip: select contracts and drag one of them onto a folder in the
              tree, or use Move to folder.
            </Typography>
          )}
        </Box>
        {/* folder ⋮ menu */}
        <Menu anchorEl={menu?.el} open={!!menu} onClose={() => setMenu(null)}>
          <MenuItem
            onClick={() => {
              if (menu)
                setNameDialog({
                  open: true,
                  mode: 'create',
                  parentId: menu.folder.id,
                });
              setMenu(null);
            }}
          >
            <CreateNewFolderOutlinedIcon fontSize="small" sx={{ mr: 1 }} /> New
            subfolder
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (menu)
                setNameDialog({
                  open: true,
                  mode: 'rename',
                  folder: menu.folder,
                });
              setMenu(null);
            }}
          >
            <EditOutlinedIcon fontSize="small" sx={{ mr: 1 }} /> Rename
          </MenuItem>
          <MenuItem
            sx={{ color: 'error.main' }}
            onClick={() => {
              if (menu) setDeleteIds([menu.folder.id]);
              setMenu(null);
            }}
          >
            <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} /> Delete
          </MenuItem>
        </Menu>

        <FolderNameDialog
          state={nameDialog}
          folders={folders}
          loading={savingName}
          onClose={() => setNameDialog({ open: false })}
          onSubmit={saveName}
        />
        <MoveDialog
          open={moveOpen}
          count={selectedIds.length}
          tree={tree}
          folders={folders}
          loading={moving}
          onClose={() => setMoveOpen(false)}
          onMove={(target) => requestMove(selectedIds, target)}
          countAlreadyIn={countSelectedIn}
        />
        <ReusableConfirmationModal
          open={!!pendingMove}
          onClose={() => setPendingMove(null)}
          title="Move from current folder?"
          cancelBtnText="No, Go Back"
          confirmBtnText="Yes, Move"
          onConfirm={() => pendingMove && moveContracts(pendingMove)}
          buttonLoading={moving}
        >
          <Stack spacing={1}>
            <Typography>
              {pendingMove?.conflicts.length} selected contract(s) are already
              in another folder:
            </Typography>
            {conflictSummary?.map(([name]) => (
              <Stack key={name} direction="row" spacing={0.5}>
                <FolderIcon fontSize="small" sx={{ color: ACCENT }} />
                <b>{name}</b>
              </Stack>
            ))}
            <Typography>
              They will be <b>removed from their current folder</b> and moved to
              the new one. A contract can only be in one folder.
            </Typography>
          </Stack>
        </ReusableConfirmationModal>
        <ReusableConfirmationModal
          open={!!removeIds}
          onClose={() => setRemoveIds(null)}
          title="Remove from folder"
          cancelBtnText="No, Go Back"
          confirmBtnText="Yes, Remove"
          onConfirm={() =>
            removeIds && removeFromFolder({ ids: removeIds }).catch(() => {})
          }
          buttonLoading={unfiling}
        >
          <Stack spacing={1}>
            <Typography>
              Remove {removeIds?.length} contract(s) from their folder?
            </Typography>
            <Typography>
              The contracts are <b>not</b> deleted. They will appear under{' '}
              <b>{STANDALONE_LABEL}</b>.
            </Typography>
          </Stack>
        </ReusableConfirmationModal>
        <ReusableConfirmationModal
          open={!!deleteIds}
          onClose={() => setDeleteIds(null)}
          title="Delete folders"
          cancelBtnText="No, Go Back"
          confirmBtnText="Yes, Delete"
          onConfirm={() => deleteIds && removeFolders(deleteIds)}
          buttonLoading={deleting}
        >
          <Stack spacing={1}>
            <Typography>
              Delete {deleteIds?.length} folder(s)
              {deleteIds?.length === 1
                ? ` “${byId.get(deleteIds[0])?.name}”`
                : ''}
              ?
            </Typography>
            <Typography>
              The contracts inside are <b>not</b> deleted. Sub-folders are
              deleted with their parent.
            </Typography>
          </Stack>
        </ReusableConfirmationModal>
      </Stack>
    </>
  );
};

export default DocumentFoldersComponent;
